import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import {
  COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import { useNotification } from "./notification.hook";
import CollaboratorsImpersonateService from "../services/api/collaborators/CollaboratorsImpersonate.service";

interface useImpersonateCollaboratorsProps {
  enabled: boolean;
}

export const useImpersonateCollaborators = ({
  enabled,
}: useImpersonateCollaboratorsProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: ["get-infinite-impersonate-collaborators", debouncedSearch],
      queryFn: ({ pageParam, signal }) =>
        CollaboratorsImpersonateService.list(
          COLLABORATORS_PAGE_SIZE,
          pageParam as number,
          [
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * COLLABORATORS_PAGE_SIZE < lastPage.pagination.total
          ? allPages.length
          : undefined,
      staleTime: RefreshReactQuery.MODERATE,
      enabled,
    });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  const clearSearch = useCallback(() => setSearch(""), []);

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  return {
    data: items,
    initLoading: isLoading,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    fetchingMore: isFetching && (data?.pages.length ?? 0) > 0,
    setSearch,
    search,
    clearSearch,
  };
};
